'use client';

/* Nav `stock` menu — a single tab that discloses the full stock-locator
   vehicle list, grouped by make. Mobile-first: on ≤767px it opens as a
   bottom sheet (big thumb targets, dimmed backdrop, body-scroll lock); on
   ≥768px it's an anchored popover under the tab. One component, the two
   layouts are pure CSS (see `.mo-stock-menu` in tokens.css); JS only
   supplies the desktop anchor coords via CSS vars.

   Rendered through a portal to <body> so the nav strip's `overflow-x:auto`
   (horizontal tab scroll) can't clip it. Because the portal lands outside
   the page's `.mo-design` wrapper, we re-assert the `mo-design` class and
   mirror any manually-pinned `data-theme` so the menu matches the page. */

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { trackNavEvent } from '@/lib/analytics/counterscale';
import { stockByGroup } from '../_data/stockVehicles';

/** Desktop popover width cap (px). The menu is sized to the nav-shell width,
    clamped to this maximum, then centered under the masthead. */
const DESKTOP_MAX_WIDTH = 680;

export function StockMenuTab({
  label,
  active,
}: {
  label: string;
  active: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [theme, setTheme] = useState<string | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();
  const menuId = useId();
  const groups = stockByGroup();

  // Portals need the DOM; gate on mount to stay SSR-safe.
  useEffect(() => setMounted(true), []);

  // Any navigation closes the menu (covers selecting a car + back/forward).
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const positionFromTrigger = useCallback(() => {
    const el = btnRef.current;
    const panel = panelRef.current;
    if (!el || !panel) return;
    const r = el.getBoundingClientRect();
    // Desktop popover: size it to the masthead — width is the nav shell's width
    // clamped to a fixed 800px max — and center it under the shell. Falls back to
    // the viewport if the shell isn't found. The mobile sheet ignores both
    // --mo-anchor-width and --mo-anchor-left (it's pinned left:0/right:0 at full
    // width), so this is a no-op there even though it still runs.
    const shell = btnRef.current?.closest<HTMLElement>('.mo-nav-shell');
    const shellRect = shell?.getBoundingClientRect();
    const shellLeft = shellRect ? shellRect.left : 0;
    const shellWidth = shellRect ? shellRect.width : window.innerWidth;
    const width = Math.min(shellWidth, DESKTOP_MAX_WIDTH);
    const left = shellLeft + (shellWidth - width) / 2;
    panel.style.setProperty('--mo-anchor-top', `${Math.round(r.bottom + 8)}px`);
    panel.style.setProperty('--mo-anchor-left', `${Math.round(left)}px`);
    panel.style.setProperty('--mo-anchor-width', `${Math.round(width)}px`);
  }, []);

  useEffect(() => {
    if (!open) return;
    positionFromTrigger();

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const prevOverflow = document.body.style.overflow;
    if (isMobile) document.body.style.overflow = 'hidden';

    // Land focus inside the menu for keyboard users.
    panelRef.current?.querySelector<HTMLElement>('a[href]')?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    const onResize = () => positionFromTrigger();
    // The desktop popover is anchored to the trigger's viewport rect, so it
    // would drift on page scroll — just close it. The mobile sheet is fixed to
    // the bottom and locks body scroll, so it's unaffected. Capture phase is
    // required to catch page scroll (scroll doesn't bubble), but it also sees
    // the menu's OWN overflow scroll — ignore those or the tall desktop list
    // closes itself the instant you try to scroll down it.
    const onScroll = (e: Event) => {
      if (window.matchMedia('(max-width: 767px)').matches) return;
      const target = e.target;
      if (target instanceof Node && panelRef.current?.contains(target)) return;
      setOpen(false);
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, positionFromTrigger]);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next) {
        // Mirror a manually-pinned theme from the page's .mo-design wrapper
        // onto the body-portal so the menu doesn't flip themes.
        setTheme(
          btnRef.current?.closest('.mo-design')?.getAttribute('data-theme') ??
            null,
        );
        trackNavEvent('stock');
      }
      return next;
    });
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={active ? 'mo-nav-tab mo-nav-tab-on' : 'mo-nav-tab'}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={toggle}
      >
        {label}
        <span aria-hidden className="mo-stock-caret">
          ▾
        </span>
      </button>

      {mounted && open
        ? createPortal(
            <div
              className="mo-design mo-stock-overlay"
              data-theme={theme ?? undefined}
              role="presentation"
            >
              <div
                className="mo-stock-backdrop"
                aria-hidden
                onClick={() => setOpen(false)}
              />
              <div
                ref={panelRef}
                id={menuId}
                role="menu"
                aria-label="Stock locator"
                className="mo-stock-menu"
              >
                <div className="mo-stock-sheet-handle" aria-hidden />
                <div className="mo-stock-menu-groups">
                  {groups.map(({ group, vehicles }) => (
                    <div key={group.id} className="mo-stock-menu-group">
                      <div className="mo-stock-group-label">
                        {group.navLabel}
                      </div>
                      {vehicles.map((v) => {
                        const href = `/stock/${v.slug}`;
                        const isActive =
                          pathname === href ||
                          !!pathname?.startsWith(`${href}/`);
                        return (
                          <Link
                            key={v.slug}
                            href={href}
                            role="menuitem"
                            className={
                              isActive
                                ? 'mo-stock-link is-active'
                                : 'mo-stock-link'
                            }
                            onClick={() => {
                              trackNavEvent(`stock_${v.event}`);
                              setOpen(false);
                            }}
                          >
                            <span
                              aria-hidden
                              className="mo-stock-cap-art"
                              style={
                                v.art
                                  ? {
                                      // 108px capsule: the 480px WebP sibling
                                      // (see carArtWebp in cars/_lib/carGenArt).
                                      backgroundImage: `url(${v.art.replace(/\.png$/, '.480.webp')})`,
                                    }
                                  : undefined
                              }
                            />
                            <span className="mo-stock-cap-name">{v.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
